import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ObjectStorage } from '@libs/libraries/shared';
import { RouterModule } from '@angular/router';
import { NgDialogsModule, NgPipesModule, NgStylingComponentsModule } from '@libs/frameworks/angular';
import { CookiesStorageFactory, CookiesStorageHandler, ICookiesStorageFactoryConfig } from '@libs/libraries/frontend';
import { EnumAddonPageAddonInjection, AddonFacebookChatScript, AddonFacebookChatService, AddonGoogleTagManagerScript, AddonGoogleTagManagerService, AddonPageAddonsRenderer } from '@domains/frontend/page-addons';
import { FormsModule } from '@angular/forms';

import { LegalCookiesBannerComponent } from './cookies-banner.component';
import { LEGAL_DUMMY_ADDON_PROVIDERS } from './cookies-banner.dummy-providers';
import { EnumLegalCookiesBannerInjectionToken, ILegalCookiesBannerModuleConfig } from './cookies-banner.interface';
import { LegalCookiesSettingsComponent } from '../shared/cookies-settings/cookies-settings.component';
import { LegalCookiesBannerStorageHandler } from '../../services/cookies-banner/cookies-storage/cookies-storage.handler';
import { ILegalCookiesBannerStorage, LEGAL_INITIAL_COOKIES_STORAGE } from '../../services/cookies-banner/cookies-storage/cookies-storage.interface';


@NgModule({
	declarations: [
		LegalCookiesBannerComponent
	],
	imports		: [
		CommonModule,
		RouterModule,
		NgStylingComponentsModule,
		LegalCookiesSettingsComponent,
		NgDialogsModule,
		NgPipesModule,
		FormsModule
	],
	exports		: [
		LegalCookiesBannerComponent
	]
})
export class LegalCookiesBannerModule {

	//** Configurations */
	private static readonly COOKIE_ID	: string = 'cookie_preferences';
	private static readonly DUMMY_CONFIG: ILegalCookiesBannerModuleConfig = {
		cookieExpiration	: 86400000,
		social				: {
			facebookConfig	: {
				customerChat: {
					enable	: false
				},
				webLogin	: false,
				pageId		: ''
			}
		},
		analytics			: {
			googleTagId		: null
		}
	};

	static register(config: ILegalCookiesBannerModuleConfig): ModuleWithProviders<LegalCookiesBannerModule> {
		return {
			ngModule	: LegalCookiesBannerModule,
			providers	: [
				AddonPageAddonsRenderer,
				{
					provide	: EnumLegalCookiesBannerInjectionToken.Config,
					useValue: config
				},
				{
					provide		: EnumLegalCookiesBannerInjectionToken.BannerCookies,
					deps		: [DOCUMENT],
					useFactory	: (document: Document) => {
						const cookiesStorageFactoryConfig: ICookiesStorageFactoryConfig<ILegalCookiesBannerStorage> = {
							id		: LegalCookiesBannerModule.COOKIE_ID,
							data	: LEGAL_INITIAL_COOKIES_STORAGE,
							expiry	: new Date(Date.now() + config.cookieExpiration)
						};
						return CookiesStorageFactory.create(cookiesStorageFactoryConfig, document);
					}
				},
				{
					provide		: LegalCookiesBannerStorageHandler,
					deps		: [EnumLegalCookiesBannerInjectionToken.BannerCookies],
					useFactory	: (cookieBannerHandler: CookiesStorageHandler) => {
						return new LegalCookiesBannerStorageHandler(
							ObjectStorage.create<ILegalCookiesBannerStorage>(
								LegalCookiesBannerModule.COOKIE_ID,
								LEGAL_INITIAL_COOKIES_STORAGE,
								cookieBannerHandler
							)
						);
					}
				},

				//** Social Media Scripts */
				{
					provide		: EnumAddonPageAddonInjection.FacebookSocialConfig,
					useValue	: config.social.facebookConfig
				},
				AddonFacebookChatScript,
				AddonFacebookChatService,

				//** Google Tag Manager Scripts */
				AddonGoogleTagManagerScript,
				AddonGoogleTagManagerService,
				{
					provide		: EnumAddonPageAddonInjection.GoogleTagManagerId,
					useValue	: config.analytics.googleTagId
				}
			]
		};
	}

	static dummy(): ModuleWithProviders<LegalCookiesBannerModule> {
		return {
			ngModule	: LegalCookiesBannerModule,
			providers	: [
				AddonPageAddonsRenderer,
				{
					provide	: EnumLegalCookiesBannerInjectionToken.Config,
					useValue: LegalCookiesBannerModule.DUMMY_CONFIG
				},
				{
					provide		: EnumLegalCookiesBannerInjectionToken.BannerCookies,
					deps		: [DOCUMENT],
					useFactory	: (document: Document) => {
						const cookiesStorageFactoryConfig: ICookiesStorageFactoryConfig<ILegalCookiesBannerStorage> = {
							id		: LegalCookiesBannerModule.COOKIE_ID,
							data	: LEGAL_INITIAL_COOKIES_STORAGE,
							expiry	: new Date(Date.now() + LegalCookiesBannerModule.DUMMY_CONFIG.cookieExpiration)
						};
						return CookiesStorageFactory.create(cookiesStorageFactoryConfig, document);
					}
				},
				{
					provide		: LegalCookiesBannerStorageHandler,
					deps		: [EnumLegalCookiesBannerInjectionToken.BannerCookies],
					useFactory	: (cookieBannerHandler: CookiesStorageHandler) => {
						return new LegalCookiesBannerStorageHandler(
							ObjectStorage.create<ILegalCookiesBannerStorage>(
								LegalCookiesBannerModule.COOKIE_ID,
								LEGAL_INITIAL_COOKIES_STORAGE,
								cookieBannerHandler
							)
						);
					}
				},

				//** Social Media Scripts */
				{
					provide		: EnumAddonPageAddonInjection.FacebookSocialConfig,
					useValue	: LegalCookiesBannerModule.DUMMY_CONFIG.social.facebookConfig
				},
				//** Google Tag Manager Scripts */
				{
					provide		: EnumAddonPageAddonInjection.GoogleTagManagerId,
					useValue	: LegalCookiesBannerModule.DUMMY_CONFIG.analytics.googleTagId
				},
				...LEGAL_DUMMY_ADDON_PROVIDERS
			]
		};
	}
}
